import type {
  PutAdminV1StoresStoreIdVariantsPricingBulkBodyItemsItem,
  PutAdminV1StoresStoreIdVariantsVariantIdPricingBody,
  StoreVariantPricing,
  StoreVariantPricingSource,
} from '@nucleus-commerce/api-client/admin';
import type { Money } from '@nucleus-commerce/core';

/** What a pricing row holds while it is edited; `skuOverride` is the raw text of the input. */
export interface PricingDraft {
  price: Money | null;
  compareAt: Money | null;
  isAvailable: boolean;
  skuOverride: string;
}

export function draftFromRow(row: StoreVariantPricing): PricingDraft {
  return {
    price: row.override?.price ?? null,
    compareAt: row.override?.compareAt ?? null,
    isAvailable: row.isAvailable,
    skuOverride: row.skuOverride ?? '',
  };
}

const sameAmount = (a: Money | null, b: Money | null) =>
  (a?.amount ?? null) === (b?.amount ?? null);

/** True when saving the draft would change something on the server. */
export function isDraftDirty(draft: PricingDraft, row: StoreVariantPricing): boolean {
  const saved = draftFromRow(row);
  return (
    !sameAmount(draft.price, saved.price) ||
    !sameAmount(draft.compareAt, saved.compareAt) ||
    draft.isAvailable !== saved.isAvailable ||
    draft.skuOverride.trim() !== saved.skuOverride
  );
}

/** The whole row is sent: a null price removes the override, a blank SKU override clears it. */
export function toPricingBody(
  draft: PricingDraft,
): PutAdminV1StoresStoreIdVariantsVariantIdPricingBody {
  return {
    price: draft.price,
    compareAt: draft.price ? draft.compareAt : null,
    isAvailable: draft.isAvailable,
    skuOverride: draft.skuOverride.trim() || null,
  };
}

/** Bulk items for the rows whose draft differs from what is saved; untouched rows are left out. */
export function toBulkItems(
  drafts: ReadonlyMap<string, PricingDraft>,
  rows: readonly StoreVariantPricing[],
): PutAdminV1StoresStoreIdVariantsPricingBulkBodyItemsItem[] {
  return rows.flatMap((row) => {
    const draft = drafts.get(row.variantId);
    return draft && isDraftDirty(draft, row)
      ? [{ variantId: row.variantId, ...toPricingBody(draft) }]
      : [];
  });
}

export type PriceSource = NonNullable<StoreVariantPricingSource> | 'none';

/** Where the effective price comes from; `none` when the store currency has no exchange rate yet. */
export function priceSource(row: StoreVariantPricing): PriceSource {
  return row.effective === null ? 'none' : (row.source ?? 'none');
}

/**
 * What the row sells at with no store price of its own: the effective price, or, while an override
 * still makes that the store's own, the base when it is in the same currency. Null when only the
 * server can tell (a converted base under an override), and until a rate exists.
 */
export function inheritedPrice(row: StoreVariantPricing): Money | null {
  if (!row.override?.price) return row.effective;
  return row.base.currency === row.override.price.currency ? row.base : null;
}

/** Cheapest and dearest effective price, or null while any variant has none. */
export function effectiveRange(
  rows: readonly StoreVariantPricing[],
): { min: Money; max: Money } | null {
  let min: Money | null = null;
  let max: Money | null = null;
  for (const row of rows) {
    if (row.effective === null) return null;
    if (!min || row.effective.amount < min.amount) min = row.effective;
    if (!max || row.effective.amount > max.amount) max = row.effective;
  }
  return min && max ? { min, max } : null;
}
